import React, {
  createContext,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';

export const getOnlineStatus = (): boolean =>
  typeof navigator !== 'undefined' ? navigator.onLine : true;

// Stable getter — identity never changes, never causes re-renders on network transition
const NetworkStatusContext = createContext<() => boolean>(getOnlineStatus);

export const NetworkStatusProvider: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <NetworkStatusContext.Provider value={getOnlineStatus}>
    {children}
  </NetworkStatusContext.Provider>
);

// Returns stable () => boolean — safe in useCallback deps, never causes re-renders
export const useNetworkStatus = () => React.useContext(NetworkStatusContext);

// Returns reactive boolean — ONLY for UI components that must re-render on network change
export const useIsOnline = (): boolean => {
  const [isOnline, setIsOnline] = useState(getOnlineStatus);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return isOnline;
};
